import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, input } from '@angular/core';
import { PageComponent } from '../../layout/page/page.component';
import { TranslationService } from '../../../core/services/translation/translation.service';
import { Router } from '@angular/router';
import { LinkLeadToUserResponse, SimulationResponse } from './simulation.model';
import { formLanguageReloader } from '../../../core/helpers/page.helper';
import { LogService } from '../../../core/services/log/log.service';
import { UserService } from '../../user-connection/user.service';
import { ApplicationStore } from '../../../core/services/application-store.service';
import { AppConfig } from '../../../app.config';
import { NoValue, PvmoUserRight } from '@tribeauto/ngx-mobilite-material';
import { getParamsFromLeadsFilters } from '../../../core/helpers/url.helper';
import { LogoData } from '../../../core/services/application.model';

@Component({
  selector: 'pvmo-simulation',
  imports: [PageComponent],
  templateUrl: './simulation.component.html',
  styleUrl: './simulation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SimulationComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translationService = inject(TranslationService);
  private readonly logService = inject(LogService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly appConfig = inject(AppConfig);
  private readonly LANDING = 'mobi-sim-landing';
  private readonly PAGE_NAME = 'page-simulation';
  token = input.required<string>();
  externalId = input.required<string>();
  comingFromSub = input<string>('false');
  isUserConnected = this.userService.isUserConnected;

  constructor() {
    effect(() => {
      const selected = this.translationService.selectedFile();
      formLanguageReloader(this.LANDING, this.PAGE_NAME, selected);
      this.cdr.markForCheck();
    });

    effect(() => {
      const userData = this.applicationStore.instant('userData')();
      let userSub: string | NoValue;
      if (this.appConfig.devOrLocal) {
        userSub = userData?.login;
      }
      this.createDetailComponent(userSub);
    });
  }

  private createDetailComponent(userSub: string | NoValue): void {
    const detailComponent = document.createElement(this.LANDING);
    detailComponent.setAttribute('nonce', 'NGINX_CSP_NONCE');
    detailComponent.setAttribute('language', this.translationService.selectedFile());
    detailComponent.setAttribute('session-uuid', this.logService.sessionUuid());
    detailComponent.setAttribute('coming-from-sub', this.comingFromSub() ?? 'false');
    detailComponent.addEventListener('backLeadsEvent', event => {
      const backLeadsEvent: boolean = (event as CustomEvent).detail;
      if (backLeadsEvent) {
        void this.router.navigate(['/leads'], {
          queryParams: getParamsFromLeadsFilters(this.applicationStore.instant('leadsFilters')())
        });
      }
    });
    detailComponent.addEventListener('themingChanged', event => {
      const logoData: LogoData = (event as CustomEvent).detail;
      this.applicationStore.update('logoData', logoData);
    });
    if (userSub) {
      detailComponent.setAttribute('user-sub', userSub);
    }
    if (this.externalId() && this.token()) {
      detailComponent.setAttribute('external-id', this.externalId());
      detailComponent.setAttribute('token', this.token());
    }
    detailComponent.addEventListener('simulationData', event => {
      const simulationResponse: SimulationResponse = (event as CustomEvent).detail;
      void this.router.navigate([`/subscription/${simulationResponse.reference}`], {
        queryParams: {
          token: simulationResponse.accessToken,
          webStoreId: simulationResponse.webStoreId
        }
      });
    });

    if (
      !this.applicationStore
        .instant('userData')()
        ?.userRights?.some(right => right.name === PvmoUserRight.PVMO_NAVIGATION_BAR)
    ) {
      detailComponent.addEventListener('linkLeadToUser', event => {
        const simData: LinkLeadToUserResponse = (event as CustomEvent).detail;
        if (simData.leadId) {
          window.parent.postMessage({ leadId: simData.leadId }, this.appConfig.urlEff);
        }
      });
    }

    detailComponent.addEventListener('returnHome', event => {
      if ((event as CustomEvent).detail) {
        void this.router.navigate(['/home']);
      }
    });

    const page = document.getElementById(this.PAGE_NAME);
    if (page) {
      page.append(detailComponent);
      this.cdr.markForCheck();
    }
  }
}
